import React, { useState } from "react";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Badge } from "@/shared/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import {
  Search,
  Calendar as CalendarIcon,
  ArrowUpFromLine,
  Copy,
  Check,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  X,
  Download,
  Building,
  Plus,
  History,
} from "lucide-react";
import EngineLayout from "@/shared/components/engine/EngineLayout";
import WithdrawalForm from "@/shared/components/withdrawals/WithdrawalForm";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const statusConfig = {
  success: { icon: CheckCircle2, color: "bg-emerald-100 text-emerald-700", label: "completed" },
  pending: { icon: Clock, color: "bg-amber-100 text-amber-700", label: "pending" },
  processing: { icon: Loader2, color: "bg-blue-100 text-blue-700", label: "processingLabel" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-700", label: "failed" },
};

export default function EngineWithdrawals() {
  const { t } = useLanguage();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const resellerName = "Gaming Partners Ltd";

  // TODO: Implement custom API calls
  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["engine-withdrawals"],
    queryFn: async () => [], // TODO: Implement Transaction.filter API call
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["engine-merchants"],
    queryFn: async () => [], // TODO: Implement Merchant.list API call
  });

  // Apply filters
  const filteredPayouts = transactions.filter(tx => {
    if (search && !tx.transaction_id?.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter !== "all" && tx.status !== statusFilter) return false;
    if (merchantFilter !== "all" && tx.merchant_id !== merchantFilter) return false;
    if (dateRange.from && new Date(tx.created_date) < dateRange.from) return false;
    if (dateRange.to && new Date(tx.created_date) > dateRange.to) return false;
    return true;
  });

  const copyToClipboard = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("all");
    setMerchantFilter("all");
    setDateRange({ from: null, to: null });
  };

  const hasFilters = search || statusFilter !== "all" || merchantFilter !== "all" || dateRange.from;

  // Calculate totals
  const stats = React.useMemo(() => {
    const success = transactions.filter(tx => tx.status === "success");
    const pending = transactions.filter(tx => tx.status === "pending" || tx.status === "processing");
    return {
      total: success.reduce((sum, tx) => sum + (tx.amount || 0), 0),
      pendingCount: pending.length,
      pendingAmount: pending.reduce((sum, tx) => sum + (tx.amount || 0), 0),
    };
  }, [transactions]);

  const handleWithdrawalSubmit = async (data) => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1500));
    setSubmitting(false);
    toast.success(`${t("withdrawalRequestSubmittedSuccessfully")} ${t("type")}: ${data.type}`);
  };

  return (
    <EngineLayout currentPage="EngineWithdrawals" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("withdrawals")}</h1>
            <p className="text-slate-500">{t("requestPayoutsViaBankUsdt")}</p>
          </div>
        </div>

        <Tabs defaultValue="new" className="space-y-6">
          <TabsList className="bg-white border p-1">
            <TabsTrigger value="new" className="gap-2 px-4">
              <Plus className="h-4 w-4" />
              {t("newWithdrawal")}
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2 px-4">
              <History className="h-4 w-4" />
              {t("history")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="new">
            <WithdrawalForm
              onSubmit={handleWithdrawalSubmit}
              submitting={submitting}
              stats={stats}
              accentColor="sky"
            />
          </TabsContent>

          <TabsContent value="history">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100"
              >
                <p className="text-sm text-slate-500">{t("totalWithdrawn")}</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">฿{stats.total.toLocaleString()}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100"
              >
                <p className="text-sm text-slate-500">{t("completed")}</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {transactions.filter(tx => tx.status === "success").length}
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100"
              >
                <p className="text-sm text-slate-500">{t("pending")}</p>
                <p className="text-2xl font-bold text-amber-600 mt-1">{stats.pendingCount}</p>
              </motion.div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 mb-4">
              <div className="flex flex-wrap gap-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={t("searchByTransactionId")}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={merchantFilter} onValueChange={setMerchantFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder={t("merchant")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allMerchants")}</SelectItem>
                    {merchants.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder={t("status")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("allStatus")}</SelectItem>
                    <SelectItem value="success">{t("completed")}</SelectItem>
                    <SelectItem value="pending">{t("pending")}</SelectItem>
                    <SelectItem value="processing">{t("processingLabel")}</SelectItem>
                    <SelectItem value="failed">{t("failed")}</SelectItem>
                  </SelectContent>
                </Select>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {dateRange.from ? (
                        dateRange.to ? (
                          `${format(dateRange.from, "MMM d")} - ${format(dateRange.to, "MMM d")}`
                        ) : format(dateRange.from, "MMM d, yyyy")
                      ) : t("dateRange")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={(range) => setDateRange(range || { from: null, to: null })}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>

                {hasFilters && (
                  <Button variant="ghost" onClick={clearFilters} className="text-slate-500">
                    <X className="h-4 w-4 mr-1" />
                    {t("clear")}
                  </Button>
                )}
              </div>
            </div>

            {/* Payouts Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("transaction")}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("merchant")}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("method")}</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("amount")}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("status")}</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{t("date")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {isLoading ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-500">{t("loading")}</td>
                      </tr>
                    ) : filteredPayouts.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-slate-500">{t("noWithdrawalsFound")}</td>
                      </tr>
                    ) : (
                      filteredPayouts.slice(0, 50).map((payout, index) => {
                        const status = statusConfig[payout.status] || statusConfig.pending;
                        const StatusIcon = status.icon;

                        return (
                          <motion.tr
                            key={payout.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.02 * index }}
                            onClick={() => setSelectedPayout(payout)}
                            className="hover:bg-slate-50 transition-colors cursor-pointer"
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-sky-100 flex items-center justify-center">
                                  <ArrowUpFromLine className="h-4 w-4 text-sky-600" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-mono text-sm font-medium">
                                      {payout.transaction_id?.slice(0, 12)}...
                                    </span>
                                    <button
                                      onClick={(e) => copyToClipboard(e, payout.transaction_id)}
                                      className="text-slate-400 hover:text-slate-600"
                                    >
                                      {copiedId === payout.transaction_id ? (
                                        <Check className="h-3.5 w-3.5 text-emerald-500" />
                                      ) : (
                                        <Copy className="h-3.5 w-3.5" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-900">{payout.merchant_code || t("nA")}</span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="outline" className="text-xs">
                                {payout.payment_method === "bank_transfer" ? t("bank") : "USDT"}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-semibold text-sky-600">฿{payout.amount?.toLocaleString()}</span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge className={`${status.color} gap-1 border-0`}>
                                <StatusIcon className={`h-3 w-3 ${payout.status === "processing" ? "animate-spin" : ""}`} />
                                {t(status.label)}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-500">
                              {payout.created_date ? format(new Date(payout.created_date), "MMM d, HH:mm") : t("nA")}
                            </td>
                          </motion.tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Payout Detail Sheet */}
        <Sheet open={!!selectedPayout} onOpenChange={() => setSelectedPayout(null)}>
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
            {selectedPayout && (
              <>
                <SheetHeader className="pb-4 border-b">
                  <SheetTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
                      <ArrowUpFromLine className="h-5 w-5 text-sky-600" />
                    </div>
                    <div>
                      <p className="text-lg">{t("withdrawal")}</p>
                      <Badge className={`${statusConfig[selectedPayout.status]?.color} border-0`}>
                        {t(statusConfig[selectedPayout.status]?.label)}
                      </Badge>
                    </div>
                  </SheetTitle>
                </SheetHeader>

                <div className="py-6 space-y-6">
                  <div className="text-center py-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500 mb-1">{t("amount")}</p>
                    <p className="text-3xl font-bold text-sky-600">
                      ฿{selectedPayout.amount?.toLocaleString()}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-slate-500">{t("merchant")}</p>
                        <p className="font-medium">{selectedPayout.merchant_code}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">{t("provider")}</p>
                        <p className="font-medium">{selectedPayout.provider_code}</p>
                      </div>
                    </div>

                    <div className="p-4 bg-slate-50 rounded-xl space-y-3">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Building className="h-4 w-4" />
                        <span className="text-sm font-medium">{t("bankDetails")}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-slate-500">{t("bank")}</p>
                          <p className="font-medium">{selectedPayout.bank_code || t("nA")}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">{t("account")}</p>
                          <p className="font-medium font-mono">{selectedPayout.bank_account || t("nA")}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-slate-500 mb-2">{t("transactionId")}</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm">
                          {selectedPayout.transaction_id}
                        </code>
                        <Button variant="outline" size="icon" onClick={(e) => copyToClipboard(e, selectedPayout.transaction_id)}>
                          {copiedId === selectedPayout.transaction_id ? (
                            <Check className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </EngineLayout>
  );
}